var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, makeObservable } from 'mobx';
import { deserialize, list, withDefault } from '../../utils/serializr/serializr';
import { AspectCollectedSets } from './modules/AspectCollectedSets';
import { AspectAttach } from './modules/evaluation/1_attach/AspectAttached';
import { AttachedTrait } from '../trait/attachedtrait/AttachedTrait';
import { Entity } from '../entity/Entity';
import { AspectInteract } from './modules/AspectInteract';
import { AspectImport } from './modules/AspectImport';
import { AspectInfo } from './modules/AspectInfo';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { AspectEvents } from './modules/events/AspectEvents';
import { AspectTraits } from './modules/evaluation/0_traits/AspectTraits';
import { AspectObserve } from './modules/AspectObserve';
import { AspectCssTransition } from './modules/cssTransition/AspectCssTransition';
import { AspectCss } from './modules/component/AspectCss';
import { TraitConditionEvaluator } from '../trait/evaluator/TraitConditionEvaluator';
import { AspectJsx } from './modules/component/AspectJsx';
import { AspectWrap } from './modules/component/AspectWrap';
import { AspectContent } from './modules/AspectContent';
import { AspectRole } from './modules/AspectRole';
import { AspectContext } from '../context/AspectContext';
import { AspectClassNames } from './modules/component/AspectClassNames';
import { object } from '../../utils/serializr/schema/object';
import { AspectGlobal } from './modules/evaluation/AspectGlobal';
import { AspectRegional } from './modules/evaluation/AspectRegional';
import { AspectLocal } from './modules/evaluation/AspectLocal';
import { editable } from '../../utils/mobx/decorators/editable';
import { strip } from '../../utils/serializr/schema/inner/strip';
import { runtime } from '../runtime/Runtime';
import { AspectTagSpecific } from './modules/tagSpecific/AspectTagSpecific';
import { removeObsoleteTraitKeys } from '../../utils/serializr/schema/specific/trait/removeObsoleteTraitKeys';
import { AspectMounts } from './modules/mount/AspectMounts';
import { AspectMount } from './modules/mount/AspectMount';
import { AspectCssAnimation } from './modules/AspectCssAnimation';
import { AspectBackgroundVideo } from './modules/AspectBackgroundVideo';
import { AspectTextTransform } from './modules/AspectTextTransform';
export class Aspect extends Entity {
    type = 'aspect';
    constructor({ parent, name }) {
        super({ parent });
        makeObservable(this);
        if (name) {
            this.name = name;
        }
    }
    traits;
    exports;
    name;
    get readableIdentifier() {
        return `${this.parent.readableIdentifier} ⮞ ${this.name}`;
    }
    get persistentAspect() {
        if (this.parent instanceof AttachedTrait)
            return this.parent.trait.ownAspect[this.name];
        if (this.parent instanceof TraitConditionEvaluator && this.parent.traitCondition) {
            return this.parent.traitCondition.ownAspect[this.name];
        }
        return this;
    }
    get actor() {
        return this.parent;
    }
    get context() {
        return new AspectContext(this);
    }
    /* modules */
    get aspectTraits() {
        return new AspectTraits(this);
    }
    get aspectImport() {
        return new AspectImport(this);
    }
    get aspectAttached() {
        return new AspectAttach(this);
    }
    get aspectDraggable() {
        return new runtime.solidPackages.Rare.classes.AspectDraggable(this);
    }
    get aspectEdit() {
        return new runtime.solidPackages.Edit.classes.AspectEdit(this);
    }
    get aspectEditContent() {
        return new runtime.solidPackages.Edit.classes.AspectEditContent(this);
    }
    get aspectEvents() {
        return new AspectEvents(this);
    }
    get aspectInteract() {
        return new AspectInteract(this);
    }
    get aspectCssAnimation() {
        return new AspectCssAnimation(this);
    }
    get aspectInfo() {
        return new AspectInfo(this);
    }
    get aspectCollectedSets() {
        return new AspectCollectedSets(this);
    }
    get aspectJsx() {
        return new AspectJsx(this);
    }
    get aspectCreateTrait() {
        return new runtime.solidPackages.Edit.classes.AspectCreateTrait(this);
    }
    get aspectSetValue() {
        return new runtime.solidPackages.Edit.classes.AspectSetValue(this);
    }
    get aspectTraitRemover() {
        return new runtime.solidPackages.Edit.classes.AspectRemoveTrait(this);
    }
    get aspectHighlight() {
        return new runtime.solidPackages.Edit.classes.AspectHighlight(this);
    }
    get aspectObserve() {
        return new AspectObserve(this);
    }
    get aspectCssTransition() {
        return new AspectCssTransition(this);
    }
    get aspectWrap() {
        return new AspectWrap(this);
    }
    get aspectCss() {
        return new AspectCss(this);
    }
    get aspectClassNames() {
        return new AspectClassNames(this);
    }
    get aspectContent() {
        return new AspectContent(this);
    }
    get aspectTagSpecific() {
        return new AspectTagSpecific(this);
    }
    get aspectTextTransform() {
        return new AspectTextTransform(this);
    }
    get aspectRole() {
        return new AspectRole(this);
    }
    get aspectStrip() {
        return new runtime.solidPackages.Edit.classes.AspectStrip(this);
    }
    get aspectBackgroundVideo() {
        return new AspectBackgroundVideo(this);
    }
    get aspectMounts() {
        return new AspectMounts(this);
    }
    get aspectMount() {
        const firstFromObservable = Array.from(this.aspectMounts.observableMounts.values()).find(mount => !mount.isClone);
        if (firstFromObservable) {
            return firstFromObservable;
        }
        const firstFromNative = Array.from(this.aspectMounts.mounts.values()).find(mount => !mount.isClone);
        if (firstFromNative) {
            return firstFromNative;
        }
        return this.dummyMount;
    }
    get dummyMount() {
        return new AspectMount(this);
    }
    /* Attribute evaluation */
    get ats() {
        return this.aspectAttached.attachedObject;
    }
    get global() {
        return new AspectGlobal(this);
    }
    get regional() {
        return new AspectRegional(this);
    }
    get local() {
        return new AspectLocal(this);
    }
    get actives() {
        return this.global.aspectActive.activeObject;
    }
    get values() {
        return this.global.aspectActive.activeValuesObject;
    }
    get atOf() {
        return this.global.atOf;
    }
    get atsOf() {
        return this.global.atsOf;
    }
    get activesOf() {
        return this.global.activesOf;
    }
    get activeOf() {
        return this.global.activeOf;
    }
    get valueOf() {
        return this.global.valueOf;
    }
    get valuesOf() {
        return this.global.valuesOf;
    }
    get scaledValueOf() {
        return this.global.scaledValueOf;
    }
    get attachedTraits() {
        return this.aspectAttached.attachedTraits;
    }
    get activeTraits() {
        return this.global.aspectActive.activeTraits;
    }
    get css() {
        return this.aspectCss.css;
    }
    get image() {
        return this.aspectContent.regional.primaryOfKey('image');
    }
    get text() {
        return this.aspectContent.regional.primaryOfKey('text');
    }
    get video() {
        return this.aspectContent.regional.primaryOfKey('video');
    }
    get box() {
        return this.aspectContent.regional.primaryOfKey('box');
    }
    static fromSerialized(ser = {}) {
        return deserialize(Aspect, ser);
    }
    static get serializeInfo() {
        return {
            extends: Entity.serializeInfo,
            props: {
                traits: withDefault(removeObsoleteTraitKeys(list(object(runtime.class.Trait))), []),
                exports: withDefault(list(object(runtime.class.Trait)), []),
            },
        };
    }
    static get stripInfo() {
        return {
            extends: Entity.stripInfo,
            props: {
                contents: strip((a) => a.aspectStrip.contents),
            },
        };
    }
}
__decorate([
    editable('shallow')
], Aspect.prototype, "traits", void 0);
__decorate([
    editable('shallow')
], Aspect.prototype, "exports", void 0);
__decorate([
    memoize
], Aspect.prototype, "persistentAspect", null);
__decorate([
    memoize
], Aspect.prototype, "context", null);
__decorate([
    memoize
], Aspect.prototype, "aspectTraits", null);
__decorate([
    memoize
], Aspect.prototype, "aspectImport", null);
__decorate([
    memoize
], Aspect.prototype, "aspectAttached", null);
__decorate([
    memoize
], Aspect.prototype, "aspectDraggable", null);
__decorate([
    memoize
], Aspect.prototype, "aspectEdit", null);
__decorate([
    memoize
], Aspect.prototype, "aspectEditContent", null);
__decorate([
    memoize
], Aspect.prototype, "aspectEvents", null);
__decorate([
    memoize
], Aspect.prototype, "aspectInteract", null);
__decorate([
    memoize
], Aspect.prototype, "aspectCssAnimation", null);
__decorate([
    memoize
], Aspect.prototype, "aspectInfo", null);
__decorate([
    memoize
], Aspect.prototype, "aspectCollectedSets", null);
__decorate([
    memoize
], Aspect.prototype, "aspectJsx", null);
__decorate([
    memoize
], Aspect.prototype, "aspectCreateTrait", null);
__decorate([
    memoize
], Aspect.prototype, "aspectSetValue", null);
__decorate([
    memoize
], Aspect.prototype, "aspectTraitRemover", null);
__decorate([
    memoize
], Aspect.prototype, "aspectHighlight", null);
__decorate([
    memoize
], Aspect.prototype, "aspectObserve", null);
__decorate([
    memoize
], Aspect.prototype, "aspectCssTransition", null);
__decorate([
    memoize
], Aspect.prototype, "aspectWrap", null);
__decorate([
    memoize
], Aspect.prototype, "aspectCss", null);
__decorate([
    memoize
], Aspect.prototype, "aspectClassNames", null);
__decorate([
    memoize
], Aspect.prototype, "aspectContent", null);
__decorate([
    memoize
], Aspect.prototype, "aspectTagSpecific", null);
__decorate([
    memoize
], Aspect.prototype, "aspectTextTransform", null);
__decorate([
    memoize
], Aspect.prototype, "aspectRole", null);
__decorate([
    memoize
], Aspect.prototype, "aspectStrip", null);
__decorate([
    memoize
], Aspect.prototype, "aspectBackgroundVideo", null);
__decorate([
    memoize
], Aspect.prototype, "aspectMounts", null);
__decorate([
    computed
], Aspect.prototype, "aspectMount", null);
__decorate([
    memoize
], Aspect.prototype, "dummyMount", null);
__decorate([
    memoize
], Aspect.prototype, "global", null);
__decorate([
    memoize
], Aspect.prototype, "regional", null);
__decorate([
    memoize
], Aspect.prototype, "local", null);
__decorate([
    memoize
], Aspect, "serializeInfo", null);
__decorate([
    memoize
], Aspect, "stripInfo", null);
